'use strict';
//***********************************************************************************
//***********************************************************************************
//**** A handler to manipulate a rectangle (creation / edition)
//***********************************************************************************
//***********************************************************************************

import { cn_add, cn_cart, cn_clone, cn_dot, cn_mul, cn_normal, cn_size, cn_sub } from '../utils/cn_utilities';
import { cn_mouse_event } from './cn_mouse_event';
import { cn_event_handler } from './cn_event_handler';
import { cn_handler_rotation } from './cn_handler_rotation';

export class cn_rectangle_handler extends cn_event_handler {
    /**
     * Constructor
     * @param {number[]} center
     * @param {number[]} size
     * @param {number} orientation
     * @param {cn_event_handler} parent
     */
    constructor(center, size, orientation, parent) {
        super(parent);
        /*** Geometry */
        this.center = cn_clone(center);
        this.size = cn_clone(size);
        this.orientation = orientation;
        this.min_size = [0, 0];

        /** Allowed operations */
        this.allow_move = true;
        this.allow_resize = true;
        this.allow_measures = true;
        this.allow_rotate = true;

        /** Mouseover */
        this._mouseover = -1;
        this._grabbed_position = [0, 0];

        /** rotation handler */
        this._rotation_handler = null;
        this._vertices = [];
        this._measures = [[0, 0], [0, 0]];
    }

    set_rotation_snap_segments(segments) {
        if (this._rotation_handler)
            this._rotation_handler._snap_segments = segments;
    }
    //*****************************************************************
    //*** Draw the handler
    //*****************************************************************
    draw(camera) {
        var html = '';

        //*** Rotation handler */
        if (this.allow_rotate) {
            if (!this._rotation_handler) {
                this._rotation_handler = new cn_handler_rotation([0, 0], 0);
                this._rotation_handler.on('change', () => {
                    this.orientation = this._rotation_handler.angle;
                    this.call("change");
                });
                this._handlers.push(this._rotation_handler);
            }
            this._rotation_handler.center = this.center;
            this._rotation_handler.angle = this.orientation;
            this._rotation_handler.world_radius = 0.6 * cn_size(this.size);
            html += this._rotation_handler.draw(camera);
        }
        else if (this._rotation_handler) {
            this.remove_handler(this._rotation_handler);
            this._rotation_handler = null;
        }

        //*** rectangle
        this._vertices = [];
        let dx = cn_cart([1, this.orientation * Math.PI / 180]);
        let dy = cn_normal(dx);
        let p = cn_sub(this.center, cn_add(cn_mul(dx, 0.5 * this.size[0]), cn_mul(dy, 0.5 * this.size[1])));
        this._vertices.push(p);
        p = cn_add(p, cn_mul(dx, this.size[0]));
        this._vertices.push(p);
        p = cn_add(p, cn_mul(dy, this.size[1]));
        this._vertices.push(p);
        p = cn_sub(p, cn_mul(dx, this.size[0]));
        this._vertices.push(p);
        html += camera.draw_path(this._vertices, "handle_outline", true);

        //*** resize handles
        if (this._mouseover >= 0 && this._mouseover < 4) {
            html += camera.draw_line_move_arrow(this._vertices[this._mouseover], this._vertices[(this._mouseover + 1) % 4]);
        }

        //*** Measures */
        if (this.allow_measures) {
            html += camera.draw_measure(this._vertices[3], this._vertices[0], this._measures[0], this._mouseover == 5);
            html += camera.draw_measure(this._vertices[2], this._vertices[3], this._measures[1], this._mouseover == 6);
        }

        return html;
    }

    //*****************************************************************
    //*** Clear move data
    //*****************************************************************
    clear_move() {
        if (this._rotation_handler) this._rotation_handler.clear_move();
        this._mouseover = -1;
    }

    click(ev) {
        if (this._mouseover == 5 || this._mouseover == 6) {
            this.call("measure_clicked", (this._mouseover == 5) ? 1 : 0);
            return true;
        }
        return false;
    }

    /**
     * Manage a passive move. To return 'true' if something of interest under the mouse.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    move(ev) {
        this.clear_move();
        if (this.allow_measures) {
            if (ev.camera.on_measure(ev.mouse_screen, this._measures[0])) {
                this._mouseover = 5;
                return true;
            }
            if (ev.camera.on_measure(ev.mouse_screen, this._measures[1])) {
                this._mouseover = 6;
                return true;
            }
        }

        if (this.allow_resize) {
            for (let n = 0; n < this._vertices.length; n++) {
                let p0 = this._vertices[n];
                let p1 = this._vertices[(n + 1) % this._vertices.length];
                if (ev.camera.on_edge(ev.mouse_world, p0, p1)) {
                    this._mouseover = n;
                    break;
                }
            }
            if (this._mouseover >= 0) return true;
        }

        if (this.allow_move) {
            let dx = cn_cart([1, this.orientation * Math.PI / 180]);
            let dy = cn_normal(dx);
            let p = cn_sub(ev.mouse_world, this.center);
            let x = cn_dot(p, dx);
            let y = cn_dot(p, dy);
            if (Math.abs(x) <= 0.5 * this.size[0] && Math.abs(y) <= 0.5 * this.size[1]) {
                this._mouseover = 4;
                return true;
            }
        }

        return super.move(ev);
    }

    /**
     * Manage a grab. To return 'true' if grab is to be managed.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    grab(ev) {
        this._grabbed_position = cn_clone(ev.mouse_world);
        if (this._mouseover >= 0 && this._mouseover <= 4) return true;
        return super.grab(ev);
    }

    /**
     * Manage a drag. Only after a grab that returned true. To return 'true' if drag had an effect.
     * @param {cn_mouse_event} ev
     * @returns  {boolean}
     */
    drag(ev) {

        //*** Resize */
        if (this._mouseover >= 0 && this._mouseover < 4) {
            let dx = cn_cart([1, this.orientation * Math.PI / 180]);
            let dy = cn_normal(dx);
            let new_size = cn_clone(this.size);
            let new_center = cn_clone(this.center);
            if (this._mouseover == 0) {
                let delta = cn_dot(dy, cn_sub(ev.mouse_world, this._vertices[0]));
                if (new_size[1] - delta < this.min_size[1]) delta = new_size[1] - this.min_size[1];
                new_size[1] -= delta;
                new_center = cn_add(new_center, cn_mul(dy, 0.5 * delta));
            }
            else if (this._mouseover == 1) {
                let delta = cn_dot(dx, cn_sub(ev.mouse_world, this._vertices[2]));
                if (new_size[0] + delta < this.min_size[0]) delta = this.min_size[0] - new_size[0];
                new_size[0] += delta;
                new_center = cn_add(new_center, cn_mul(dx, 0.5 * delta));
            }
            else if (this._mouseover == 2) {
                let delta = cn_dot(dy, cn_sub(ev.mouse_world, this._vertices[2]));
                if (new_size[1] + delta < this.min_size[1]) delta = this.min_size[1] - new_size[1];
                new_size[1] += delta;
                new_center = cn_add(new_center, cn_mul(dy, 0.5 * delta));
            }
            else if (this._mouseover == 3) {
                let delta = cn_dot(dx, cn_sub(ev.mouse_world, this._vertices[0]));
                if (new_size[0] - delta < this.min_size[0]) delta = new_size[0] - this.min_size[0];
                new_size[0] -= delta;
                new_center = cn_add(new_center, cn_mul(dx, 0.5 * delta));
            }

            this.center = new_center;
            this.size = new_size;
            this.call("change");
            return true;
        }

        //*** Move */
        if (this._mouseover == 4) {
            let delta = cn_sub(ev.mouse_world, this._grabbed_position);
            this._grabbed_position = cn_clone(ev.mouse_world);
            this.center = cn_add(this.center, delta);
            this.call("change");
            return true;
        }

        return super.drag(ev);
    }
}

